import { useId } from 'react'
import { useT } from '~/lib/i18n'
import { cn } from '~/lib/utils'

/**
 * A hex nut, viewed head-on. Chosen over a generic "shop" glyph because it reads at
 * 16px, works as a favicon, and its geometry is reused for avatars (`.hex`) so the
 * whole interface feels cut from one part.
 */
export function HexNut({ className }: { className?: string }) {
  // React's useId emits «r0»-style ids, which are not valid inside `url(#…)`.
  const gradientId = `nut${useId().replace(/\W/g, '')}`
  return (
    <svg
      viewBox="0 0 32 32"
      className={cn('size-8', className)}
      aria-hidden
      focusable="false"
      role="presentation"
    >
      <defs>
        {/* Literal hex rather than theme tokens: the mark is fixed artwork and has
            to match public/favicon.svg exactly. */}
        <linearGradient id={gradientId} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#f8bb4d" />
          <stop offset="55%" stopColor="#ef8f2b" />
          <stop offset="100%" stopColor="#cd671d" />
        </linearGradient>
      </defs>
      <path d="M16 1.6 28.5 8.8v14.4L16 30.4 3.5 23.2V8.8z" fill={`url(#${gradientId})`} />
      {/* Chamfer: a lighter inner face, the way a real nut catches the light. */}
      <path
        d="M16 4.6 25.9 10.3v11.4L16 27.4 6.1 21.7V10.3z"
        fill="#ffffff"
        fillOpacity="0.16"
        stroke="#6b3d12"
        strokeOpacity="0.25"
        strokeWidth="0.75"
      />
      <circle cx="16" cy="16" r="6.1" fill="#241a12" />
      <circle
        cx="16"
        cy="16"
        r="6.1"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.2"
        strokeWidth="1"
      />
      {/* Thread ticks inside the bore. */}
      <path
        d="M16 10.6v1.6M16 19.8v1.6M10.6 16h1.6M19.8 16h1.6"
        stroke="#ffffff"
        strokeOpacity="0.3"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Wordmark({ className }: { className?: string }) {
  const t = useT()
  return (
    <span className={cn('leading-none font-extrabold tracking-tight', className)}>
      {t('brand.hardware')}
      {/* The entity has to stay in JSX: a catalog string is not HTML-decoded. */}
      <span className="text-ink-muted">&nbsp;{t('brand.mediator')}</span>
    </span>
  )
}

export function Lockup({ subtitle, className }: { subtitle?: string; className?: string }) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <HexNut className="size-8 shrink-0" />
      <div className="min-w-0">
        <Wordmark className="block truncate text-[0.9375rem]" />
        {subtitle ? (
          <span className="stencil mt-1 block truncate text-ink-subtle">{subtitle}</span>
        ) : null}
      </div>
    </div>
  )
}

const TEETH = [0, 45, 90, 135, 180, 225, 270, 315]

/**
 * A cog that advances in eight discrete steps rather than spinning smoothly — one
 * tooth per step, so it ratchets. Small thing, but it is the loading state users
 * see most often, and it costs nothing.
 */
export function Cog({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('size-5 animate-ratchet', className)}
      role="status"
      aria-label={label}
    >
      <g fill="currentColor" fillRule="evenodd">
        {TEETH.map((angle) => (
          <rect
            key={angle}
            x="10.75"
            y="1.1"
            width="2.5"
            height="4.6"
            rx="0.8"
            transform={`rotate(${angle} 12 12)`}
          />
        ))}
        <path d="M12 4.4a7.6 7.6 0 1 0 0 15.2 7.6 7.6 0 0 0 0-15.2Zm0 4.3a3.3 3.3 0 1 0 0 6.6 3.3 3.3 0 0 0 0-6.6Z" />
      </g>
    </svg>
  )
}

/**
 * Initials in the same hexagon as the mark. `size` is the box; the clip does the
 * rest. Tone `primary` for the signed-in user, `muted` everywhere else.
 */
export function HexAvatar({
  initials,
  className,
  tone = 'primary',
}: {
  initials: string
  className?: string
  tone?: 'primary' | 'muted'
}) {
  return (
    <span
      className={cn(
        'hex grid size-9 shrink-0 place-items-center text-xs font-bold',
        // No ring: `clip-path` crops box-shadow too, so the outline would vanish.
        tone === 'primary' ? 'bg-primary-soft text-primary' : 'bg-surface-sunken text-ink-muted',
        className,
      )}
      aria-hidden
    >
      {initials}
    </span>
  )
}
